import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Tema (RNF-09): claro u oscuro segun el sistema y, si el sistema no dice
 * nada, claro. La app se usa sobre todo a plena luz en el parque, recien
 * acabado el entreno; el suelo de caucho negro queda para quien lo pida o
 * tenga el movil en modo oscuro.
 *
 * Solo se guarda la eleccion EXPLICITA del usuario. Antes se guardaba el tema
 * en cada arranque, asi que un valor guardado no distinguia "lo elegi" de "era
 * el de por defecto" y un cambio de valor por defecto nunca llegaba a nadie.
 * Por eso la clave es nueva. El script de index.html repite esta logica antes
 * del primer pintado para que no haya destello.
 */

type Theme = 'dark' | 'light';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
  /** El sistema pide movimiento reducido (RNF-05 / 3.4). */
  reducedMotion: boolean;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);
const STORAGE_KEY = 'cp.theme-choice';

/** Color de la barra del navegador: el suelo de cada tema del parque. */
const THEME_COLOR: Record<Theme, string> = { dark: '#0C0D0E', light: '#E8E9E5' };

function systemTheme(): Theme {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function readStoredChoice(): Theme | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light') return stored;
  } catch {
    // Ventana privada o almacenamiento bloqueado.
  }
  return null;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [choice, setChoice] = useState<Theme | null>(readStoredChoice);
  const [system, setSystem] = useState<Theme>(systemTheme);
  const [reducedMotion, setReducedMotion] = useState(false);
  const theme = choice ?? system;

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    // La barra del navegador acompana al suelo.
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme]);
  }, [theme]);

  // Mientras el usuario no elija, el tema sigue al del sistema en vivo.
  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setSystem(query.matches ? 'dark' : 'light');
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  const setTheme = useCallback((next: Theme) => {
    setChoice(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* la eleccion dura solo esta sesion */
    }
  }, []);

  const toggle = useCallback(
    () => setTheme(theme === 'dark' ? 'light' : 'dark'),
    [setTheme, theme],
  );

  const value = useMemo(
    () => ({ theme, setTheme, toggle, reducedMotion }),
    [theme, setTheme, toggle, reducedMotion],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme debe usarse dentro de ThemeProvider');
  return context;
}
